/* eslint-disable react/prop-types */
import React, {memo} from 'react';
import {BrowserRouter, Redirect, Route, RouteProps, Switch} from 'react-router-dom';
import {map, omit} from 'lodash';
import {AdminRole} from '~/redux/admins/types';
import {IconType} from 'react-icons/lib';
import {CustomSwitch} from '~/components/elements/CustomSwitch';

export interface BasicRoute extends RouteProps {
  position?: 'left' | 'right';
  path: string;
  redirect?: string;
  routes?: BasicRoute[];
  action?: 'new-tab';
  label?: string;
  icon?: IconType;
  class?: string;
  permissions?: string[];
}

export interface PrivateRoute extends BasicRoute {
  routes?: PrivateRoute[];
}

export interface AppRouterProps {
  basename?: string;
  authorities?: AdminRole[];
  defaultRedirect: string;
  privateRoutes?: PrivateRoute[];
  publicRoutes?: BasicRoute[];
  notFoundPage?: React.FunctionComponent<any>;
  unauthorizedPage?: React.FunctionComponent<any>;
}

interface Params {
  methodKey: string;
  token: string;
}

const omitRouteRenderProps = (route: BasicRoute) => {
  return omit(route, ['render', 'component']);
};

const NotFoundPageDefault = () => {
  return (
    <div>
      <h1>This is a not found page by default</h1>
    </div>
  );
};

const UnauthorizedPageDefault = () => {
  return (
    <div>
      <h1>This is an unauthorized page by default</h1>
    </div>
  );
};

export const AppRouter = memo(function AppRouter({
  basename,
  authorities,
  defaultRedirect,
  privateRoutes = [],
  publicRoutes = [],
  notFoundPage: NotFoundPage = NotFoundPageDefault,
  unauthorizedPage: UnauthorizedPage = UnauthorizedPageDefault,
}: AppRouterProps) {
  const renderRedirectRoute = (route: BasicRoute) => (
    <Route
      key={`redirect-${route.path}`}
      {...omitRouteRenderProps(route)}
      render={() => <Redirect to={route.redirect || defaultRedirect} />}
    />
  );

  const renderPublicRoute = (route: BasicRoute, parentPath = '') => {
    const {
      path,
      component: RouteComponent = ({children}: {children: React.ReactNode}) => <div>{children}</div>,
      exact,
    } = route;
    const fullPath = parentPath && parentPath !== '/' ? `${parentPath}${path}` : path;

    // Handle redirect URLs
    if (route.redirect) {
      return renderRedirectRoute(route);
    }

    return !route.routes ? (
      <Route
        key={`public-${fullPath}`}
        exact={exact === undefined ? true : exact}
        {...omitRouteRenderProps(route)}
        path={fullPath}
        render={props => <RouteComponent {...props} />}
      />
    ) : (
      <Route
        key={`public-${fullPath}`}
        exact={exact === undefined ? true : exact}
        {...omitRouteRenderProps(route)}
        path={fullPath}
        render={props => (
          <RouteComponent {...props}>
            <Switch>{route?.routes?.map(subRoute => renderPublicRoute(subRoute, fullPath))}</Switch>
          </RouteComponent>
        )}
      />
    );
  };

  const renderNotFoundRoute = () => {
    return <Route path="*" key="notfound-route" render={props => <NotFoundPage {...props} />} />;
  };

  return (
    <BrowserRouter basename={basename}>
      <CustomSwitch>
        {map(publicRoutes, route => renderPublicRoute(route))}
        {renderNotFoundRoute()}
      </CustomSwitch>
    </BrowserRouter>
  );
});
